import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { AvailableTabProperties } from '../../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  fetchCharingPointWithoutPagination,
  setSelectedDevice,
} from '../../../redux/slices/DeviceManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import MyButton from '../../../shared-components/button/Button';
import StationSidePanel from '../../../shared-components/table-side-panel/LocationSidePanel';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import device from '../../../utils/api/device';
import deviceHubApi from '../../../utils/api/device-hub';
import AddLocation from '../../location-pages/location-management/components/AddLocation';
import AddDevice from '../charger-management/components/AddDevice';

import DeviceDetails from './components/device-details/DeviceDetails';
import EditDeviceForm from './components/device-details/EditDeviceForm';
import TransactionCharts from './components/device-details/TransactionCharts';
import DeviceManagementTabs from './components/DeviceManagementTabs';
import DeviceSearch from './components/DeviceSearch';
import OcppConsole from './components/ocpp-console/OcppConsole';

/**
 * Component to display the device management page
 * @returns {any} The rendered device management page element.
 */
function DeviceManagement() {
  const { t, i18n } = useTranslation('devices');
  const location = useLocation();
  const { t: tPartner } = useTranslation('partnerDeviceManagement');
  const { t: tStation } = useTranslation('station');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { tab: deviceTab, id } = useParams<{ tab: string; id: string }>();
  const { selectedChargingPoint } = useAppSelector((s) => s.device);
  const { type } = useParams<{ type: string }>();
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const searchParameters = new URLSearchParams(location.search);
  const isPersonalCharger =
    location.pathname === `/ev-admin/charger-management/${id}/device-details` &&
    searchParameters.get('type') === 'personal-charger';
  const [containerTabs, setContainerTabs] = useState<AvailableTabProperties[]>([
    {
      label: t('deviceDetails'),
      key: 'device-details',
      isActive: false,
    },
    {
      label: t('oCPPConsole'),
      key: 'ocpp-console',
      isActive: false,
    },
    {
      label: t('reports', 'Reports'),
      key: 'reports',
      isActive: false,
    },
  ]);
  const [stationPanelOpen, setStationPanelOpen] = useState(false);
  const [isStationEditMode, setIsStationEditMode] = useState(false);
  const [selectedDeviceGeneral, setSelectedDeviceGeneral] = useState<any>(null);

  const stationId =
    selectedDeviceGeneral && typeof (selectedDeviceGeneral as any).chargingStation === 'object'
      ? (selectedDeviceGeneral as any).chargingStation?._id ||
        (selectedDeviceGeneral as any).chargingStation?.id
      : selectedDeviceGeneral?.chargingStation;

  const [sidePanelState, setSidePanelState] = useState<{
    open: boolean;
    title: string;
    content: JSX.Element | null;
  }>({
    open: false,
    title: '',
    content: null,
  });

  /**
   * fetching the device details
   */
  const fetchDeviceDetails = async () => {
    const activeId = id || selectedChargingPoint?.id;
    if (!activeId) return;
    try {
      if (location.pathname.includes('ev-device-hub')) {
        const response = await deviceHubApi.fetchAdvanceDeviceDetails(activeId);
        const data = response?.data || response || {};
        const connectorsMapped = (data.connectorInformation || []).map(
          (conn: any, index: number) => {
            let ratingValue = '';
            if (conn.connectorRating) {
              ratingValue =
                typeof conn.connectorRating === 'object'
                  ? conn.connectorRating.capacityKW
                  : conn.connectorRating;
            }
            return {
              ...conn,
              connectorId: conn.connectorId || index + 1,
              status: conn.status || 'unknown',
              connectorRating: ratingValue,
            };
          },
        );
        setSelectedDeviceGeneral({
          ...data,
          id: data._id || data.id,
          chargingPointName: data.serialNumber || '',
          chargingPointSn: data.serialNumber || '',
          chargePointVendor: data.brandName || '',
          chargePointModel: data.modelName || '',
          vendor: data.brandName || '',
          model: data.modelName || '',
          maxPowerAvailable: data.specifications?.powerOutput
            ? `${Number(data.specifications.powerOutput).toFixed(3)} kW`
            : '',
          maximumOutputPower: data.specifications?.powerOutput
            ? String(data.specifications.powerOutput)
            : '0',
          maximumOutputPowerUnit: 'kW',
          serialNumber: data.serialNumber || '',
          tenantId: data.tenantName || '',
          reachableState: data.status || '',
          positionType: data.installationType || '',
          currentType: data.currentType || '',
          connectors: connectorsMapped,
        });
        dispatch(
          setSelectedDevice({
            id: data._id || data.id || activeId,
            name: data.serialNumber || '',
            chargingPointSn: data.serialNumber || '',
            modelSeries: data.modelSeries || '',
          }),
        );
      } else {
        const response = await device.fetchChargingPointGeneralDetails(activeId);
        const enelData = response?.data?.enelData || {};
        const stationNameValue =
          response?.data?.stationName ||
          response?.data?.chargingStationName ||
          response?.data?.chargingStation?.name ||
          response?.data?.chargingStation?.stationName ||
          response?.data?.chargingStation?.chargingStationName ||
          '';
        const partnerNameValue =
          response?.data?.partnerName ||
          response?.data?.accountName ||
          response?.data?.account?.accountName ||
          response?.data?.account?.name ||
          response?.data?.partner?.partnerName ||
          response?.data?.partner?.name ||
          response?.data?.chargingStation?.accountName ||
          response?.data?.chargingStation?.account?.accountName ||
          response?.data?.chargingStation?.account?.name ||
          response?.data?.chargingStation?.partnerName ||
          response?.data?.chargingStation?.partner?.name ||
          response?.data?.cpoName ||
          response?.data?.tenantName ||
          response?.data?.tenantId ||
          '';
        setSelectedDeviceGeneral({
          ...response.data,
          stationName: stationNameValue,
          partnerName: partnerNameValue,
          maxPowerAvailable: response?.data?.maximumOutputPower
            ? `${Number(response.data.maximumOutputPower).toFixed(3)} ${response.data.maximumOutputPowerUnit || ''}`
            : '',
          serialNumber: enelData.sn !== undefined ? String(enelData.sn) : undefined,
          tenantId: enelData.tenantId !== undefined ? String(enelData.tenantId) : undefined,
          maxPowerPoc:
            enelData.powerMaxPoc !== undefined ? String(enelData.powerMaxPoc) : undefined,
          reachableState:
            enelData.isReachableState !== undefined ? String(enelData.isReachableState) : undefined,
          acVoltage:
            enelData.acElectricCurrent?.voltage !== undefined
              ? String(enelData.acElectricCurrent.voltage)
              : undefined,
          positionType:
            enelData.csTechnicalConfiguration?.address?.position?.type !== undefined
              ? String(enelData.csTechnicalConfiguration.address.position.type)
              : undefined,
          rvxg: enelData.isRVxG !== undefined ? String(enelData.isRVxG) : undefined,
          rvxgAlgorithm:
            enelData.rvxgAlgorithm !== undefined ? String(enelData.rvxgAlgorithm) : undefined,
          rvxgLoadAreaId:
            enelData.rvxgLoadAreaId !== undefined ? String(enelData.rvxgLoadAreaId) : undefined,
          rvxgLoadAreaName:
            enelData.rvxgLoadAreaName !== undefined ? String(enelData.rvxgLoadAreaName) : undefined,
        });
        const sn =
          response?.data?.chargingPointSn ||
          response?.data?.serialNumber ||
          (enelData.sn !== undefined ? String(enelData.sn) : '');
        dispatch(
          setSelectedDevice({
            id: response?.data?._id || response?.data?.id || activeId,
            name: response?.data?.chargingPointName || response?.data?.chargingPointSn || '',
            chargingPointSn: sn,
            modelSeries: response?.data?.modelSeries || '',
          }),
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDeviceDetails();
  }, [id, selectedChargingPoint?.id]);

  /**
   * Function to close the active side panel
   */
  const closeSidePanel = () => {
    setSidePanelState({
      open: false,
      title: '',
      content: null,
    });
  };

  /**
   * Action handler to open the Add Charger side panel or navigate to the add-device route
   */
  const handleAddCharger = () => {
    if (location.pathname.includes('ev-device-hub')) {
      navigate('/ev-admin/ev-device-hub/add-device');
    } else {
      setSidePanelState({
        open: true,
        title: ' ',
        content: (
          <AddDevice
            setSidePanelOpen={(open) => {
              if (!open) {
                closeSidePanel();
                dispatch(fetchCharingPointWithoutPagination({ chargingPointName: '' }));
              }
            }}
          />
        ),
      });
    }
  };

  /**
   * Action handler to open the Add Station side panel
   */
  const handleAddStation = () => {
    setSidePanelState({
      open: true,
      title: tStation('addStation', 'Add Station'),
      content: (
        <AddLocation
          onClose={closeSidePanel}
          onSuccess={() => {
            dispatch(fetchCharingPointWithoutPagination({ chargingPointName: '' }));
          }}
        />
      ),
    });
  };

  /**
   * Action handler to open the edit charger side panel
   */
  const handleEditCharger = () => {
    if (location.pathname.includes('ev-device-hub')) {
      navigate(`/ev-admin/ev-device-hub/edit-device/${id || selectedChargingPoint?.id}`);
    } else {
      setSidePanelState({
        open: true,
        title: tPartner('editDevice', 'Edit Charger'),
        content: (
          <EditDeviceForm
            deviceDetails={selectedDeviceGeneral || selectedChargingPoint}
            closeModal={closeSidePanel}
            onSuccess={() => {
              closeSidePanel();
              dispatch(fetchCharingPointWithoutPagination({ chargingPointName: '' }));
              fetchDeviceDetails();
            }}
          />
        ),
      });
    }
  };

  /**
   * Function to switch the tab
   * @param {string} key The key of the tab to switch to.
   * @param {boolean} navigateRequire Whether to navigate to the new tab URL.
   */
  const handleSwitchTab = (key: string, navigateRequire: boolean = true) => {
    const isDeviceHub = location.pathname.includes('ev-device-hub');
    const isExpanded =
      location.pathname.includes('charger-expanded') ||
      location.pathname.includes('device-expanded');
    const basePathForEndConsumer = isExpanded ? 'charger-expanded' : 'charger';
    let basePathForGlobal = isExpanded ? 'charger-expanded' : 'charger-management';
    if (isDeviceHub) {
      basePathForGlobal = 'ev-device-hub/device-details';
    }
    const sourcePath = location.state?.source || new URLSearchParams(location.search).get('source');

    setContainerTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          if (navigateRequire) {
            const queryParameters = new URLSearchParams(location.search);
            queryParameters.delete('operation');
            queryParameters.delete('hideSidebar');
            queryParameters.delete('resetType');
            if (sourcePath && !queryParameters.has('source')) {
              queryParameters.set('source', sourcePath);
            }
            if (selectedChargingPoint?.name && !queryParameters.has('searchBy')) {
              queryParameters.set('searchBy', selectedChargingPoint.name);
            }
            const searchString = `?${queryParameters.toString()}`;

            if (type && userCustomId) {
              navigate(
                {
                  pathname: `/ev-admin/end-consumer/${type}/profile/${userCustomId}/${basePathForEndConsumer}/${id}/${tab.key}`,
                  search: searchString,
                },
                { state: { source: sourcePath } },
              );
            } else {
              navigate(
                {
                  pathname: `/ev-admin/${basePathForGlobal}/${id}/${tab.key}`,
                  search: searchString,
                },
                { state: { source: sourcePath } },
              );
            }
          }
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  useEffect(() => {
    handleSwitchTab(deviceTab || 'device-details', false);
  }, [deviceTab]);

  useEffect(() => {
    dispatch(setPageTitle({ title: t('deviceManagement'), count: null, icon: null }));
  }, [dispatch, t]);

  useEffect(() => {
    setContainerTabs((previousTabs) =>
      previousTabs.map((tab) => {
        if (tab.key === 'device-details') {
          return { ...tab, label: t('deviceDetails') };
        }
        if (tab.key === 'ocpp-console') {
          return { ...tab, label: t('oCPPConsole') };
        }
        if (tab.key === 'reports') {
          return { ...tab, label: t('reports', 'Reports') };
        }
        return tab;
      }),
    );
  }, [t]);

  return (
    <div className="device-management-wrap" dir={i18n.dir()}>
      <div className="device-management-container">
        <DeviceManagementTabs
          containerTabs={containerTabs}
          handleSwitchTab={handleSwitchTab}
          deviceId={id || selectedChargingPoint?.id}>
          <div className="device-header-actions">
            <DeviceSearch />
            <MyButton
              label={t('charger', 'Charger')}
              buttonType="outline"
              svgIcon={<PlusIcon />}
              onClickFunc={handleAddCharger}
              disableButton={
                !permissionCheck('partnerChargingDevicesAdd') &&
                !permissionCheck('cpoChargingPointAdd')
              }
            />
            <MyButton
              label={t('charger', 'Charger')}
              buttonType="outline"
              svgIcon={<EditIcon />}
              onClickFunc={handleEditCharger}
              disableButton={
                !permissionCheck('partnerChargingDevicesUpdate') &&
                !permissionCheck('cpoChargingPointUpdate')
              }
            />
            {!isPersonalCharger && (
              <MyButton
                label={tStation('addStation', 'Add Station')}
                buttonType="primary"
                onClickFunc={handleAddStation}
                disableButton={
                  !permissionCheck('partnerChargingStationAdd') &&
                  !permissionCheck('cpoChargingStationAdd')
                }
              />
            )}
            {!isPersonalCharger && (
              <MyButton
                label={tStation('editStation', 'Edit Station')}
                buttonType="primary-outline"
                disableButton={
                  !stationId ||
                  (!permissionCheck('partnerChargingStationUpdate') &&
                    !permissionCheck('cpoChargingStationUpdate'))
                }
                onClickFunc={() => {
                  setIsStationEditMode(true);
                  setStationPanelOpen(true);
                }}
              />
            )}
          </div>
        </DeviceManagementTabs>
        <div className="device-management-content">
          {(() => {
            const activeTab = containerTabs.find((tab) => tab.isActive)?.key || 'device-details';

            let deviceContent = null;
            switch (activeTab) {
              case 'device-details':
                deviceContent = <DeviceDetails />;
                break;
              case 'ocpp-console':
                deviceContent = <OcppConsole />;
                break;
              case 'reports':
                deviceContent = <TransactionCharts />;
                break;
              default:
                deviceContent = <DeviceDetails />;
                break;
            }
            return deviceContent;
          })()}
        </div>
      </div>
      <SidePaneModel
        open={sidePanelState.open}
        title={sidePanelState.title}
        content={sidePanelState.content ?? <div />}
        closePanel={closeSidePanel}
      />
      <SidePaneModel
        open={stationPanelOpen}
        hideHeader
        content={
          <StationSidePanel
            fetchIdFrom="props"
            id={stationId}
            initialIsEditMode={isStationEditMode}
            onSuccess={() => {
              fetchDeviceDetails();
            }}
            onClose={() => setStationPanelOpen(false)}
          />
        }
        closePanel={() => setStationPanelOpen(false)}
      />
    </div>
  );
}

export default DeviceManagement;
